import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { PhaseFormOutputDTO, Subprocess } from "./types";
import { phaseFormService } from "./phaseFormService";
import { ApiError } from "@/types/api";

// Async Thunks
export const fetchPhaseForms = createAsyncThunk(
  "phaseForm/fetchAll",
  async () => {
    const response = await phaseFormService.getAll();
    return response.data;
  }
);

export const fetchPhaseFormById = createAsyncThunk(
  "phaseForm/fetchById",
  async (id: string) => {
    const response = await phaseFormService.getById(id);
    return response.data;
  }
);

export const fetchPhaseFormsByProduct = createAsyncThunk(
  "phaseForm/fetchByProduct",
  async (productId: string) => {
    const response = await phaseFormService.getByProductId(productId);
    return response.data;
  }
);

// Sem o rejectWithValue o Redux rejeita com um SerializedError e o `payload` da acao
// fica undefined - a tela lia `payload.message` e estourava um TypeError, virando um
// "Erro inesperado ao salvar os dados" que escondia o motivo real vindo do backend.
// Devolvemos o corpo do ApiError (code / message / validationErrors) para a tela poder
// apontar exatamente qual campo reprovou.
const extrairApiError = (error: any): ApiError => {
  const corpo = error?.response?.data;
  if (corpo && typeof corpo === "object" && corpo.message) {
    return corpo as ApiError;
  }
  return {
    code: "UNKNOWN_ERROR",
    error: "UNKNOWN_ERROR",
    message: error?.message || "Nao foi possivel falar com o servidor",
    statusCode: error?.response?.status || 0,
  } as ApiError;
};

export const createPhaseForm = createAsyncThunk(
  "phaseForm/create",
  async (data: Partial<PhaseFormOutputDTO>, { rejectWithValue }) => {
    try {
      const response = await phaseFormService.create(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(extrairApiError(error));
    }
  }
);

export const updatePhaseForm = createAsyncThunk(
  "phaseForm/update",
  async (
    { id, data }: { id: string; data: Partial<PhaseFormOutputDTO> },
    { rejectWithValue }
  ) => {
    try {
      const response = await phaseFormService.update(id, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(extrairApiError(error));
    }
  }
);

export const deletePhaseForm = createAsyncThunk(
  "phaseForm/delete",
  async (id: string) => {
    await phaseFormService.delete(id);
    return id;
  }
);

interface PhaseFormState {
  items: PhaseFormOutputDTO[];
  currentItem: PhaseFormOutputDTO | null;
  subprocesses: {
    items: Subprocess[];
    total: number;
    loading: boolean;
    error: string | null;
  };
  loading: boolean;
  error: string | null;
}

const initialState: PhaseFormState = {
  items: [],
  currentItem: null,
  subprocesses: {
    items: [],
    total: 0,
    loading: false,
    error: null,
  },
  loading: false,
  error: null,
};

const phaseFormSlice = createSlice({
  name: "phaseForm",
  initialState,
  reducers: {
    setPhaseForms: (state, action: PayloadAction<PhaseFormOutputDTO[]>) => {
      state.items = action.payload;
    },
    setCurrentPhaseForm: (
      state,
      action: PayloadAction<PhaseFormOutputDTO | null>
    ) => {
      state.currentItem = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    // Subprocess actions
    setSubprocesses: (
      state,
      action: PayloadAction<{ items: Subprocess[]; total: number }>
    ) => {
      state.subprocesses.items = action.payload.items;
      state.subprocesses.total = action.payload.total;
    },
    addSubprocess: (state, action: PayloadAction<Subprocess>) => {
      state.subprocesses.items.push(action.payload);
      state.subprocesses.total += 1;
    },
    removeSubprocess: (state, action: PayloadAction<string>) => {
      state.subprocesses.items = state.subprocesses.items.filter(
        (subprocess) => subprocess.id !== action.payload
      );
      state.subprocesses.total -= 1;
    },
    updateSubprocessStatus: (
      state,
      action: PayloadAction<{ id: string; status: any }>
    ) => {
      const subprocess = state.subprocesses.items.find(
        (sp) => sp.id === action.payload.id
      );
      if (subprocess) {
        subprocess.status = action.payload.status;
      }
    },
    setSubprocessLoading: (state, action: PayloadAction<boolean>) => {
      state.subprocesses.loading = action.payload;
    },
    setSubprocessError: (state, action: PayloadAction<string | null>) => {
      state.subprocesses.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPhaseForms.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchPhaseForms.fulfilled,
        (state, action: PayloadAction<PhaseFormOutputDTO[]>) => {
          state.items = action.payload;
          state.loading = false;
        }
      )
      .addCase(fetchPhaseForms.rejected, (state, action) => {
        state.error = action.error.message || "Failed to fetch phase forms";
        state.loading = false;
      })
      .addCase(
        fetchPhaseFormById.fulfilled,
        (state, action: PayloadAction<PhaseFormOutputDTO>) => {
          state.currentItem = action.payload;
        }
      )
      .addCase(fetchPhaseFormsByProduct.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchPhaseFormsByProduct.fulfilled,
        (state, action: PayloadAction<PhaseFormOutputDTO[]>) => {
          state.items = action.payload;
          state.loading = false;
        }
      )
      .addCase(fetchPhaseFormsByProduct.rejected, (state, action) => {
        state.error =
          action.error.message || "Failed to fetch phase forms by product";
        state.loading = false;
      })
      .addCase(
        createPhaseForm.fulfilled,
        (state, action: PayloadAction<PhaseFormOutputDTO>) => {
          state.items.push(action.payload);
        }
      )
      .addCase(
        updatePhaseForm.fulfilled,
        (state, action: PayloadAction<PhaseFormOutputDTO>) => {
          const index = state.items.findIndex(
            (item) => item._id === action.payload._id
          );
          if (index !== -1) {
            state.items[index] = action.payload;
          }
          if (state.currentItem?._id === action.payload._id) {
            state.currentItem = action.payload;
          }
        }
      )
      .addCase(
        deletePhaseForm.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.items = state.items.filter(
            (item) => item._id !== action.payload
          );
        }
      );
  },
});

export const {
  setPhaseForms,
  setCurrentPhaseForm,
  setLoading,
  setError,
  setSubprocesses,
  addSubprocess,
  removeSubprocess,
  updateSubprocessStatus,
  setSubprocessLoading,
  setSubprocessError,
} = phaseFormSlice.actions;

export default phaseFormSlice.reducer;
